import { lazy } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';

import { AppLayout } from '@/app/layouts/AppLayout';
import { AdminGuard } from '@/app/layouts/AdminGuard';
import { AuthGuard } from '@/app/layouts/AuthGuard';
import { TenantGuard } from '@/app/layouts/TenantGuard';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { LoginPage } from '@/pages/login/LoginPage';
import { SelectTenantPage } from '@/pages/tenant/SelectTenantPage';
import { RouterErrorPage } from '@/shared/ui/RouterErrorPage';

/* One route per navigation destination, plus the case workspace every queue row
   points at. Every route in the cockpit is a built screen.

   Built screens are lazy: each one is its own chunk, so a cockpit that grows to
   two dozen screens still opens on the dashboard alone. The layout's Suspense
   boundary covers the wait.

   There is deliberately no /orders route, no /queue/conflicts route and no
   /scenarios route: the cockpit never raises a ProductOrder, conflicted cases
   surface under Needs attention, and the scenario catalogue was a prototype
   device rather than an operator screen. All three fall through to not-found. */

const OverviewPage = lazy(async () => ({
  default: (await import('@/pages/overview/OverviewPage')).OverviewPage,
}));

const IntakePage = lazy(async () => ({
  default: (await import('@/pages/intake/IntakePage')).IntakePage,
}));

/* The five work-queue scopes are one screen: same rows, same columns, a
   different question each time. The scope travels as a prop rather than as a
   path parameter, so an unknown /queue/... path still falls through to
   not-found instead of silently rendering "My queue". */
const QueuePage = lazy(async () => ({
  default: (await import('@/pages/queue/QueuePage')).QueuePage,
}));

const MriReviewPage = lazy(async () => ({
  default: (await import('@/pages/review/MriReviewPage')).MriReviewPage,
}));

const ConversationsPage = lazy(async () => ({
  default: (await import('@/pages/review/ConversationsPage')).ConversationsPage,
}));

/* Same arrangement as the work queue: the four routed queues are one screen,
   with the queue passed as a prop rather than read from the path. */
const RoutedPage = lazy(async () => ({
  default: (await import('@/pages/routed/RoutedPage')).RoutedPage,
}));

const ServiceCatalogPage = lazy(async () => ({
  default: (await import('@/pages/catalog/ServiceCatalogPage')).ServiceCatalogPage,
}));

const CustomersPage = lazy(async () => ({
  default: (await import('@/pages/catalog/CustomersPage')).CustomersPage,
}));

/* The requester register has its own route rather than a card on Customers &
   sites: it is the only master-data screen that is written to, and a screen an
   operator maintains needs an address they can be sent to. */
const RequesterMastersPage = lazy(async () => ({
  default: (await import('@/pages/catalog/RequesterMastersPage')).RequesterMastersPage,
}));

/* The administration screens that carry a detail view — characteristics and
   templates — read which record is open from a search parameter rather than a
   path segment. One route each, no navigation entry per record, and the choice
   is still linkable and survives a reload. */
const MriCharacteristicsPage = lazy(async () => ({
  default: (await import('@/pages/admin/MriCharacteristicsPage')).MriCharacteristicsPage,
}));

const ClassificationRulesPage = lazy(async () => ({
  default: (await import('@/pages/admin/ClassificationRulesPage')).ClassificationRulesPage,
}));

const BusinessRulesPage = lazy(async () => ({
  default: (await import('@/pages/admin/BusinessRulesPage')).BusinessRulesPage,
}));

const ClarificationTemplatesPage = lazy(async () => ({
  default: (await import('@/pages/admin/ClarificationTemplatesPage')).ClarificationTemplatesPage,
}));

const ThresholdsPage = lazy(async () => ({
  default: (await import('@/pages/admin/ThresholdsPage')).ThresholdsPage,
}));

/* Tenancy administration. Both screens are reachable only by an operator whose
   sign-in carried the administrator role — the sidebar hides them and the
   service refuses them — but they are ordinary lazy routes all the same. A
   route that exists and answers 403 is a better account of "you may not" than a
   route that pretends the screen was never built. */
const TenantsPage = lazy(async () => ({
  default: (await import('@/pages/admin/TenantsPage')).TenantsPage,
}));

const TenantAccessPage = lazy(async () => ({
  default: (await import('@/pages/admin/TenantAccessPage')).TenantAccessPage,
}));

const TenantGroupsPage = lazy(async () => ({
  default: (await import('@/features/tenant-groups')).TenantGroupsPage,
}));

const AnalyticsPage = lazy(async () => ({
  default: (await import('@/pages/insight/AnalyticsPage')).AnalyticsPage,
}));

const ReportsPage = lazy(async () => ({
  default: (await import('@/pages/insight/ReportsPage')).ReportsPage,
}));

const SettingsPage = lazy(async () => ({
  default: (await import('@/pages/insight/SettingsPage')).SettingsPage,
}));

const JobsPage = lazy(async () => ({
  default: (await import('@/features/jobs')).JobsPage,
}));

/* The workspace behind every case id in every grid. One screen for all six
   tabs: they read the same case, and the tab travels in the path so a link to
   a particular decision survives being pasted into a ticket. */
const CaseWorkspacePage = lazy(async () => ({
  default: (await import('@/pages/case/CaseWorkspacePage')).CaseWorkspacePage,
}));

const routes: readonly RouteObject[] = [
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouterErrorPage />,
  },

  /* Signed in, and not yet scoped to anything. Guarded on identity alone and
     deliberately outside `AppLayout`: the shell reads the session and every
     count in it, and the service answers those reads with 403 until a tenant has
     been chosen. Eager rather than lazy, like `/login` — it is on the critical
     path of every cold sign-in, and a chunk fetched between the callback and the
     first screen is a blank page nobody asked for. */
  {
    path: '/select-tenant',
    element: (
      <AuthGuard>
        <SelectTenantPage />
      </AuthGuard>
    ),
    errorElement: <RouterErrorPage />,
  },

  /* Protected routes — AuthGuard redirects to /login when not authenticated,
     TenantGuard to /select-tenant when authenticated without a tenant */
  {
    path: '/',
    element: (
      <AuthGuard>
        <TenantGuard>
          <AppLayout />
        </TenantGuard>
      </AuthGuard>
    ),
    errorElement: <RouterErrorPage />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: 'intake', element: <IntakePage /> },
      { path: 'queue', element: <QueuePage scope="mine" /> },
      { path: 'queue/team', element: <QueuePage scope="team" /> },
      { path: 'queue/unassigned', element: <QueuePage scope="unassigned" /> },
      { path: 'queue/awaiting', element: <QueuePage scope="awaiting" /> },
      { path: 'queue/attention', element: <QueuePage scope="attention" /> },

      { path: 'mri-review', element: <MriReviewPage /> },
      { path: 'conversations', element: <ConversationsPage /> },

      { path: 'routed/macd', element: <RoutedPage queue="macd" /> },
      { path: 'routed/catalog', element: <RoutedPage queue="catalog" /> },
      { path: 'routed/non-order', element: <RoutedPage queue="non-order" /> },
      { path: 'routed/parked', element: <RoutedPage queue="parked" /> },

      { path: 'catalog/service', element: <ServiceCatalogPage /> },
      { path: 'catalog/customers', element: <CustomersPage /> },
      { path: 'catalog/requesters', element: <RequesterMastersPage /> },

      { path: 'analytics', element: <AnalyticsPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'admin/jobs', element: <AdminGuard><JobsPage /></AdminGuard> },

      /* Without a tab the workspace opens the first its queue offers. With one,
         it opens that — which is how Pixie and every grid row deep-link into a
         particular decision. */
      { path: 'case/:id', element: <CaseWorkspacePage /> },
      { path: 'case/:id/:tab', element: <CaseWorkspacePage /> },

      { path: '*', element: <NotFoundPage /> },
    ],
  },

  /* Tenant-agnostic admin routes — authenticated but no tenant required.
     These pages show platform-wide configuration (MRI characteristics, rules,
     templates, thresholds) that is shared across all tenants.

     The three tenancy administration screens live here too. They read and
     write in the platform scope rather than a tenant's — the transport's
     tenancy methods are the only ones that answer for a session with no tenant
     chosen yet — so a TenantGuard in front of them would block the one operator
     who needs them most: an administrator signing into a deployment that has no
     tenants yet, who must create the first one before there is anything to
     choose. */
  {
    path: '/',
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    errorElement: <RouterErrorPage />,
    children: [
      { path: 'admin/mri', element: <MriCharacteristicsPage /> },
      { path: 'admin/classification', element: <ClassificationRulesPage /> },
      { path: 'admin/rules', element: <BusinessRulesPage /> },
      { path: 'admin/templates', element: <ClarificationTemplatesPage /> },
      { path: 'admin/thresholds', element: <ThresholdsPage /> },
      { path: 'admin/tenants', element: <TenantsPage /> },
      { path: 'admin/access', element: <TenantAccessPage /> },
      { path: 'admin/tenant-groups', element: <TenantGroupsPage /> },
    ],
  },
];

export const router = createBrowserRouter([...routes]);
